import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, chat } from '../../theme';
import { useAppContext } from '../../contexts/AppContext';
import { apiService } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { formatNumber } from '../../utils/rtl';
import { createTranslator } from '../../i18n';
import { useThemedRTLStyles } from '../../hooks/useThemedRTLStyles';

export interface ProductOrderCardProps {
  orderId: string;
  content?: string;
  isOutgoing?: boolean;
}

const ORDER_STATUS_VALUES = ['pending', 'placed', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'] as const;

export const ProductOrderCard: React.FC<ProductOrderCardProps> = ({
  orderId,
  content,
  isOutgoing = false,
}) => {
  const styles = useThemedRTLStyles(getStyles);
  const { navigateTo, showNotification } = useAppContext();
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const orderStatusLabels: Record<string, string> = {
    pending: t('card.statusPending'),
    placed: t('card.statusPlaced'),
    confirmed: t('card.statusConfirmedOrder'),
    processing: t('card.statusProcessing'),
    shipped: t('card.statusShipped'),
    delivered: t('card.statusDelivered'),
    cancelled: t('card.statusCancelledOrder'),
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }
      try {
        const res = await apiService.getOrderById(orderId);
        if (!cancelled && res?.data) setOrder(res.data);
      } catch (_) {
        if (!cancelled) setOrder(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  const handleViewOrder = () => {
    navigateTo('order-edit', orderId);
  };

  const handleModifyOrder = () => {
    navigateTo('order-edit', orderId);
  };

  const handleChangeStatus = () => {
    if (!order) return;
    Alert.alert(
      t('card.orderStatus'),
      t('card.selectNewStatus'),
      [
        ...ORDER_STATUS_VALUES.map((value) => ({
          text: orderStatusLabels[value] || value,
          onPress: () => updateOrderStatus(value),
        })),
        { text: t('card.cancel'), style: 'cancel' },
      ]
    );
  };

  const updateOrderStatus = async (orderStatus: string) => {
    if (!orderId) return;
    setUpdating(true);
    try {
      await apiService.updateOrder(orderId, { orderStatus });
      showNotification('success', t('card.statusUpdated'), t('card.orderStatusUpdated', { status: orderStatus }));
      const res = await apiService.getOrderById(orderId);
      if (res?.data) setOrder(res.data);
    } catch (e: any) {
      showNotification('error', t('card.updateFailed'), e?.message || t('card.updateOrderFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const cardStyles = isOutgoing ? styles.cardOutgoing : styles.cardIncoming;
  const textStyles = isOutgoing ? styles.textOutgoing : styles.textIncoming;

  if (loading) {
    return (
      <View style={[styles.card, cardStyles]}>
        <ActivityIndicator size="small" color={isOutgoing ? chat.out.accent : colors.primary} />
        <Text style={[styles.label, textStyles]}>{t('card.order')}</Text>
      </View>
    );
  }

  return (
    <View style={[styles.card, cardStyles]}>
      <View style={styles.header}>
        <Ionicons name="receipt-outline" size={20} color={isOutgoing ? chat.out.accent : colors.primary} />
        <Text style={[styles.title, textStyles]}>
          {order ? `${t('card.order')} ${order.orderNumber || orderId}` : t('card.order')}
        </Text>
      </View>
      {content ? (
        <Text style={[styles.content, textStyles]} numberOfLines={2}>
          {content}
        </Text>
      ) : null}
      {order ? (
        <View style={styles.meta}>
          <Text style={[styles.metaText, textStyles]}>
            {t('card.orderStatusLabel')}{order.orderStatus || '—'}{t('card.orderPayment')}{order.paymentStatus || '—'}
          </Text>
          {order.totalPrice != null && (
            <Text style={[styles.metaText, textStyles]}>
              {t('card.orderTotal')}{order.currency || ''} {formatNumber(Number(order.totalPrice), String(locale || 'en').split(/[_-]/)[0], { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
            </Text>
          )}
        </View>
      ) : null}
      <View style={styles.actions}>
        <TouchableOpacity
          style={[styles.actionBtn, isOutgoing ? styles.actionBtnOutgoing : styles.actionBtnIncoming]}
          onPress={handleViewOrder}
          disabled={updating}
        >
          <Ionicons name="eye-outline" size={16} color={isOutgoing ? chat.out.accent : colors.white} />
          <Text style={[styles.actionLabel, { color: isOutgoing ? chat.out.accent : colors.white }]}>
            {t('card.viewOrder')}
          </Text>
        </TouchableOpacity>
        {order ? (
          <>
            <TouchableOpacity
              style={[styles.actionBtn, isOutgoing ? styles.actionBtnOutgoing : styles.actionBtnIncoming]}
              onPress={handleChangeStatus}
              disabled={updating}
            >
              {updating ? (
                <ActivityIndicator size="small" color={isOutgoing ? chat.out.accent : colors.white} />
              ) : (
                <>
                  <Ionicons name="list-outline" size={16} color={isOutgoing ? chat.out.accent : colors.white} />
                  <Text style={[styles.actionLabel, { color: isOutgoing ? chat.out.accent : colors.white }]}>
                    {t('card.statusTitle')}
                  </Text>
                </>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionBtn, isOutgoing ? styles.actionBtnOutgoing : styles.actionBtnIncoming]}
              onPress={handleModifyOrder}
              disabled={updating}
            >
              <Ionicons name="create-outline" size={16} color={isOutgoing ? chat.out.accent : colors.white} />
              <Text style={[styles.actionLabel, { color: isOutgoing ? chat.out.accent : colors.white }]}>
                {t('card.modifyOrder')}
              </Text>
            </TouchableOpacity>
          </>
        ) : null}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    borderRadius: 20,
    padding: 14,
    maxWidth: '92%',
  },
  cardOutgoing: {
    backgroundColor: chat.out.bg,
  },
  cardIncoming: {
    backgroundColor: colors.background.secondary,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  label: {
    fontSize: typography.fontSize.xs,
    opacity: 0.9,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
  },
  content: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
    opacity: 0.95,
  },
  meta: {
    marginBottom: spacing.sm,
  },
  metaText: {
    fontSize: typography.fontSize.xs,
    opacity: 0.9,
  },
  textOutgoing: {
    color: chat.out.text,
  },
  textIncoming: {
    color: colors.text.primary,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
  },
  actionBtnOutgoing: {
    backgroundColor: chat.out.chip,
  },
  actionBtnIncoming: {
    backgroundColor: colors.primary,
  },
  actionLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
});
